/**
 * BriefStudio — "tell Rammy what you want, it works out how to make it".
 *
 * Every other door in Studio asks you to already know which tool you need.
 * This one takes a sentence ("three product shots of my candle and a 10-second
 * vertical clip, with a voiceover") and comes back with a plan: what it will
 * make, which engine makes each piece, and what the whole thing costs.
 *
 * Nothing is spent until you tap Build it — the plan step is free, and the
 * prices you read are the prices the backend quoted, not a guess made here.
 */
import { useEffect, useRef, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ShareSheet from "./ShareSheet";

const SHAPES = [
  { id: "9:16", label: "Vertical", hint: "Reels, Shorts, TikTok" },
  { id: "16:9", label: "Wide", hint: "YouTube" },
  { id: "1:1", label: "Square", hint: "Feed" },
];

const KIND_META = {
  image: { icon: L.Image, label: "Picture" },
  animate: { icon: L.Wand2, label: "Picture that moves" },
  video: { icon: L.Clapperboard, label: "Footage" },
  voiceover: { icon: L.AudioLines, label: "Voiceover" },
};

const EXAMPLES = [
  "Three product photos of a soy candle on cream marble, plus a 10-second vertical clip for Reels",
  "A 30-second explainer for my dog-walking service: voiceover and a vertical clip",
  "A moody portrait of a cellist on a dim stage, then make it move for eight seconds",
];

const absolute = (path) =>
  path?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

export function BriefStudio({ onBack }) {
  const [brief, setBrief] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const makePlan = async () => {
    if (brief.trim().length < 6) { toast.error("Tell Rammy a bit more"); return; }
    setPlanning(true);
    setPlan(null);
    try {
      const { data } = await api.post("/brief/plan", { prompt: brief.trim(), aspect });
      setPlan(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setPlanning(false);
    }
  };

  const build = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/brief/${plan.id}/run`);
      setPlan(data);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/brief/${plan.id}`);
          setPlan(r.data);
          if (["done", "partial", "error"].includes(r.data.status)) {
            clearInterval(pollRef.current);
            if (r.data.status === "done") toast.success("All done — it's in your Creations too");
            else if (r.data.status === "partial") toast.warning("Some of it came out — see below");
            else toast.error("None of that came out — try rewording the brief");
          }
        } catch (_) { /* a blip shouldn't kill the poll */ }
      }, 5000);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const running = plan?.status === "running";
  const doneCount = (plan?.items || []).filter((i) => i.status === "done").length;

  return (
    <div className="max-w-3xl mx-auto p-8 md:p-10" data-testid="studio-brief">
      <button
        onClick={onBack}
        className="text-[12px] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] inline-flex items-center gap-1.5 mb-6"
        data-testid="studio-brief-back"
      >
        <L.ArrowLeft size={13} /> All of Studio
      </button>

      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-cyan-dark)]">
        Describe it → Rammy builds it
      </div>
      <h1 className="font-display text-3xl sm:text-4xl mt-3">Just say what you want.</h1>
      <p className="mt-3 text-[var(--rammy-text-sec)] leading-relaxed">
        Rammy works out whether that's pictures, footage, a voiceover or all three, picks
        the engine for each piece and prices it. You read the plan before anything is spent.
      </p>

      <Textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        rows={4}
        maxLength={1200}
        placeholder="Three product photos of a soy candle on cream marble, plus a 10-second vertical clip for Reels"
        className="mt-6 rounded-2xl resize-y border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
        data-testid="studio-brief-prompt"
      />

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="studio-brief-examples">
        {EXAMPLES.map((ex, i) => (
          <button
            key={i}
            onClick={() => setBrief(ex)}
            className="text-left text-[11.5px] px-3 py-1.5 rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] transition-colors"
            data-testid={`studio-brief-example-${i}`}
          >
            {ex.length > 52 ? `${ex.slice(0, 52)}…` : ex}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-2">
          Shape
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="studio-brief-shapes">
          {SHAPES.map((s) => (
            <button
              key={s.id}
              onClick={() => setAspect(s.id)}
              title={s.hint}
              aria-pressed={aspect === s.id}
              className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                aspect === s.id
                  ? "bg-[var(--rm-ink-900)] text-white border-[var(--rm-ink-900)]"
                  : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
              }`}
              data-testid={`studio-brief-shape-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={makePlan}
        disabled={planning || running}
        className="btn-pill btn-primary h-11 px-5 mt-6"
        data-testid="studio-brief-plan"
      >
        {planning
          ? <><L.Loader2 size={15} className="animate-spin mr-1.5" /> Working it out…</>
          : <><L.Sparkles size={15} className="mr-1.5" /> Show me the plan</>}
      </Button>

      {plan && (
        <div className="mt-8 rounded-3xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-6" data-testid="studio-brief-plan-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
                {plan.status === "planned" ? "Here's my plan" : "Building"}
              </div>
              <div className="font-display text-xl mt-1">{plan.title}</div>
              {plan.summary && (
                <p className="text-[13px] text-[var(--rammy-text-sec)] mt-1">{plan.summary}</p>
              )}
            </div>
            <div className="text-right">
              <div className="font-mono text-sm" data-testid="studio-brief-total">
                {plan.total_credits.toLocaleString()} credits
              </div>
              {plan.available_credits != null && (
                <div className="text-[11px] text-[var(--rammy-text-muted)]">
                  you have {plan.available_credits.toLocaleString()}
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 space-y-2">
            {plan.items.map((item) => {
              const meta = KIND_META[item.kind] || KIND_META.image;
              const Icon = meta.icon;
              return (
                <div
                  key={item.idx}
                  className="rounded-2xl border border-[var(--rammy-border)] p-4"
                  data-testid={`studio-brief-item-${item.idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center shrink-0">
                      {item.status === "running"
                        ? <L.Loader2 size={15} className="animate-spin" />
                        : item.status === "done"
                          ? <L.Check size={15} />
                          : <Icon size={15} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-[11.5px] text-[var(--rammy-text-muted)] mt-0.5">
                        {meta.label} · {item.engine}
                        {item.seconds ? ` · ${item.seconds}s` : ""} · {item.credits} credits
                      </div>
                      <p className="text-[12.5px] text-[var(--rammy-text-sec)] mt-1.5 leading-relaxed">
                        {item.prompt}
                      </p>
                      {item.error && (
                        <div className="text-[12px] text-red-500 mt-1.5" data-testid={`studio-brief-item-error-${item.idx}`}>
                          {item.error}
                        </div>
                      )}
                      {item.status === "done" && item.url && (
                        <div className="mt-3">
                          {item.kind === "image" ? (
                            <img
                              src={absolute(item.url)}
                              alt={item.label}
                              className="w-full max-h-[360px] object-contain rounded-xl bg-black/5"
                              data-testid={`studio-brief-image-${item.idx}`}
                            />
                          ) : item.kind === "voiceover" ? (
                            <audio src={absolute(item.url)} controls className="w-full" />
                          ) : (
                            <video
                              src={absolute(item.url)}
                              controls
                              className="w-full max-h-[380px] rounded-xl bg-black"
                              data-testid={`studio-brief-video-${item.idx}`}
                            />
                          )}
                          <Button
                            onClick={() => setShareItem({
                              id: item.creation_id,
                              title: item.label,
                              kind: item.kind === "voiceover" ? "audio"
                                : item.kind === "image" ? "image" : "video",
                            })}
                            variant="outline"
                            className="btn-pill h-8 text-xs mt-2"
                            data-testid={`studio-brief-share-${item.idx}`}
                          >
                            <L.Share2 size={13} className="mr-1.5" /> Share
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {plan.status === "planned" && (
            <div className="mt-6 rounded-2xl bg-[var(--rm-ink-900)] text-white p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm">
                  {plan.items.length} thing{plan.items.length === 1 ? "" : "s"} to make.
                </div>
                <div className="font-mono text-[11px] text-white/45 mt-1">
                  {plan.total_credits.toLocaleString()} credits · pictures land in seconds, footage takes minutes
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={makePlan}
                  className="text-[12px] text-white/60 hover:text-white transition-colors"
                  data-testid="studio-brief-replan"
                >
                  Plan it again
                </button>
                <Button
                  onClick={build}
                  disabled={starting}
                  className="btn-pill btn-primary h-11 px-5"
                  data-testid="studio-brief-build"
                >
                  {starting
                    ? <><L.Loader2 size={15} className="animate-spin mr-1.5" /> Starting…</>
                    : <><L.Hammer size={15} className="mr-1.5" /> Build it</>}
                </Button>
              </div>
            </div>
          )}

          {running && (
            <div className="mt-6 text-[12.5px] text-[var(--rammy-text-sec)]" data-testid="studio-brief-progress">
              <L.Loader2 size={13} className="inline animate-spin mr-1.5" />
              {doneCount} of {plan.items.length} done — you can leave this page, everything
              lands in Creations.
            </div>
          )}
        </div>
      )}

      {shareItem?.id && (
        <ShareSheet creation={shareItem} onClose={() => setShareItem(null)} />
      )}
    </div>
  );
}

export default BriefStudio;
